import { useEffect, useState, useRef, useCallback } from 'react'
import { useDebouncedCallback } from 'use-debounce'

export function useActiveSection(
  sectionIds: { id: string }[],
  topOffset = 0, // this shifts the page virtually down by the amount of the top offset
  mobileStickyNavOffset = 0, // to account for open sticky nav height on mobile
  isMobile: boolean,
) {
  const [activeId, setActiveId] = useState<string>(sectionIds[0]?.id ?? '')
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedSetActiveId = useDebouncedCallback(setActiveId, 300)
  const observer = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    const handleIntersect: IntersectionObserverCallback = entries => {
      // Sort by vertical position in viewport
      if (isScrollingRef.current) return
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

      if (visible.length > 0) {
        debouncedSetActiveId(visible[0]?.target?.id)
      }
    }

    observer.current = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: `-${topOffset}px 0px -70% 0px`,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    })

    return () => {
      observer.current?.disconnect()
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [topOffset, debouncedSetActiveId])

  useEffect(() => {
    if (!observer.current) return () => {}

    sectionIds.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.current?.observe(el)
    })

    return () => {
      sectionIds.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (el && observer.current) observer.current.unobserve(el)
      })
    }
  }, [sectionIds, topOffset])

  const scrollToSection = useCallback(
    (id: string) => {
      const el = document.getElementById(id)
      if (!el) return
      isScrollingRef.current = true

      setActiveId(id)

      const y =
        el.getBoundingClientRect().top +
        window.scrollY -
        topOffset -
        (isMobile ? mobileStickyNavOffset : 0)

      window.scrollTo({ top: y, behavior: 'smooth' })

      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false
      }, 1000)
    },
    [topOffset, isMobile, mobileStickyNavOffset],
  )

  return { activeId, scrollToSection }
}
